import React, { useState, useContext } from "react";
import { ShoppingBag, ArrowLeft, Store, ChefHat } from "lucide-react";
import MainLayout from "../components/MainLayout";
import { useQuery } from "@apollo/client";
import { GET_RESTAURANT_ORDERS, GET_ACTIVE_RESTAURANTS } from "../apollo/Queries";
import OrdersTable from "../components/ContributionsWithdraw";
import { AuthContext } from "../context";
import { BASE_URL } from "../store/urls";

export default function ContributionPage() {
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const { authenticatedUser } = useContext(AuthContext);
  const isSuperAdmin = authenticatedUser?.userType === "SUPER_ADMIN";

  // Fetch restaurants belonging to the user
  const { data: restaurantData, loading: loadingRestaurants } = useQuery(GET_ACTIVE_RESTAURANTS, {
    variables: {
      name: "",
      myRestaurants: isSuperAdmin
    },
    fetchPolicy: "cache-and-network"
  });

  // Fetch orders for the selected restaurant
  const { data: orderData, loading: loadingOrders, refetch: refetchOrders } = useQuery(GET_RESTAURANT_ORDERS, {
    variables: {
      uuid: selectedRestaurant?.uuid || "",
      size: 10,
      page: 0
    },
    skip: !selectedRestaurant
  });

  const restaurants = restaurantData?.getActiveRestaurants?.content || [];
  const orders = orderData?.restaurantOrders?.content || [];

  return (
    <MainLayout page="Orders">
      <div className="container px-6 py-10 mx-auto animate-fade-in-up">
        {!selectedRestaurant ? (
          // Restaurant Selection View
          <div className="space-y-10">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
              <div>
                <h2 className="text-4xl font-black text-premium-teal tracking-tighter">Order <span className="text-premium-gold">Management</span></h2>
                <div className="flex items-center gap-2 mt-2">
                  <div className="w-2 h-2 rounded-full bg-premium-gold animate-pulse"></div>
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.3em]">Select a restaurant to manage orders</p>
                </div>
              </div>
            </div>

            {loadingRestaurants ? (
              <div className="flex flex-col justify-center items-center h-[40vh] gap-6">
                <div className="w-16 h-16 rounded-[2rem] border-4 border-premium-gold/10 border-t-premium-gold animate-spin"></div>
                <p className="text-[10px] font-black text-premium-gold uppercase tracking-[0.6em] animate-pulse">Fetching restaurants...</p>
              </div>
            ) : restaurants.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 text-white">
                {restaurants.map((res) => (
                  <div
                    key={res.uuid}
                    onClick={() => setSelectedRestaurant(res)}
                    className="group relative bg-[#0D3B3F] p-8 rounded-[3rem] cursor-pointer hover:scale-[1.02] transition-all duration-500 shadow-2xl overflow-hidden"
                  >
                    <div className="absolute top-0 right-0 w-32 h-32 bg-premium-gold/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 group-hover:bg-premium-gold/20 transition-all"></div>

                    <div className="relative z-10 flex flex-col h-full space-y-6">
                      <div className="w-16 h-16 rounded-2xl overflow-hidden border-2 border-premium-gold/20">
                        <img
                          src={`${BASE_URL}${res.coverPhoto}`}
                          alt={res.name}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        />
                      </div>

                      <div>
                        <h3 className="text-2xl font-black tracking-tighter mb-1 line-clamp-1">{res.name}</h3>
                        <p className="text-[10px] font-bold text-premium-gold uppercase tracking-[0.2em] opacity-60 group-hover:opacity-100 transition-opacity">Restaurant</p>
                      </div>

                      <div className="pt-4 flex items-center justify-between border-t border-white/5">
                        <div className="flex items-center gap-3">
                          <ChefHat size={16} className="text-premium-gold" />
                          <span className="text-xs font-medium text-gray-300">Active Kitchen</span>
                        </div>
                        <div className="bg-premium-gold/10 p-2 rounded-xl group-hover:bg-premium-gold transition-colors">
                          <ArrowLeft size={16} className="text-premium-gold group-hover:text-premium-teal rotate-180" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20 grayscale opacity-40">
                <Store size={48} className="mx-auto mb-6 text-gray-200" />
                <p className="text-gray-400 font-light italic">No registered restaurants identified.</p>
              </div>
            )}
          </div>
        ) : (
          // Orders Management View
          <div className="space-y-10">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
              <div className="space-y-4">
                <button
                  onClick={() => setSelectedRestaurant(null)}
                  className="flex items-center gap-2 text-[10px] font-black text-premium-gold uppercase tracking-[0.3em] hover:translate-x-[-4px] transition-transform"
                >
                  <ArrowLeft size={14} /> Back to Selection
                </button>
                <div>
                  <h2 className="text-4xl font-black text-premium-teal tracking-tighter">
                    {selectedRestaurant.name} <span className="text-premium-gold">Orders</span>
                  </h2>
                  <div className="flex items-center gap-2 mt-2">
                    <div className="w-2 h-2 rounded-full bg-premium-gold animate-pulse"></div>
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.3em]">Live order stream</p>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4 bg-white/50 backdrop-blur-md p-2 rounded-[1.5rem] border border-gray-100 shadow-sm">
                <div className="p-3 bg-premium-teal rounded-xl text-premium-gold hover:rotate-12 transition-transform cursor-pointer" onClick={() => refetchOrders()}>
                  <ShoppingBag size={20} />
                </div>
                <div className="pr-6">
                  <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest leading-none">Total Orders</p>
                  <p className="text-lg font-black text-premium-teal tracking-tighter">{orders.length} Active</p>
                </div>
              </div>
            </div>

            <div className="bg-white/40 backdrop-blur-xl rounded-[3rem] p-8 border border-white/60 shadow-2xl shadow-premium-teal/5">
              {loadingOrders ? (
                <div className="flex flex-col justify-center items-center h-64 gap-6">
                  <div className="w-12 h-12 rounded-2xl border-4 border-premium-gold/10 border-t-premium-gold animate-spin"></div>
                  <p className="text-[10px] font-black text-premium-gold uppercase tracking-widest animate-pulse">Syncing order data...</p>
                </div>
              ) : orders.length > 0 ? (
                <OrdersTable data={orders} />
              ) : (
                <div className="text-center py-20 grayscale opacity-40">
                  <ShoppingBag size={48} className="mx-auto mb-6 text-gray-200" />
                  <p className="text-gray-400 font-light italic">No orders found for this restaurant yet.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}

